import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message } from 'antd'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { PressureTestSubInspectionsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useMemo } from 'react'

const requiredPressureTestSubInspectionsFields = ['pressureTestSubInspection']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.pressureTestSubInspectionsData,
    [formData.pressureTestSubInspectionsData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page
      size='A4'
      style={PDFStyles.page}
      id='pressure-test-sub-inspections'
      wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'pressure-test-sub-inspections', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Pressure Test Sub Inspections' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.pressureTestSubInspection ? (
            <Text style={{ fontSize: 10, lineHeight: 1.5 }}>
              {data.pressureTestSubInspection}
            </Text>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<PressureTestSubInspectionsData>()
  const { formData, handlePressureTestSubInspectionsFinish } =
    useVisualReportOps()

  const onFinish = useCallback(
    async (values: PressureTestSubInspectionsData) => {
      try {
        await handlePressureTestSubInspectionsFinish(
          values.pressureTestSubInspection || ''
        )
        message.success('Pressure test sub-inspections saved successfully!')
      } catch {
        message.error('Failed to save pressure test sub-inspections')
      }
    },
    [handlePressureTestSubInspectionsFinish]
  )

  const initialValues = React.useMemo(
    () => formData.pressureTestSubInspectionsData,
    [formData.pressureTestSubInspectionsData]
  )

  return (
    <StyledForm<PressureTestSubInspectionsData>
      form={form}
      name='pressureTestSubInspectionsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='pressureTestSubInspection'
        label='Pressure Test Sub-Inspection Details'
        rules={[
          {
            required: true,
            message: 'Please enter pressure test sub-inspection details',
          },
        ]}>
        <Input.TextArea
          rows={6}
          placeholder='Enter pressure test inspection details...'
        />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Pressure Test Sub-Inspections
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const PressureTestSubInspectionsSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'pressureTestSubInspections',
  description: 'Pressure test inspection details',
  title: 'Pressure Test Sub-Inspections',
  requiredFields: requiredPressureTestSubInspectionsFields,
}

export const PressureTestSubInspectionsSectionPDF = RenderPDF
